import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../academicConfiguration/AcademicCreate.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
  CustomDropdown,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const HolidayCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.ID || null;

  const { translations } = useSelector((state) => state.language);

  // Get user data from TokenService
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [holidayTypes, setHolidayTypes] = useState([]);
  const [formData, setFormData] = useState({
    holidayType: "",
    fromDate: null,
    toDate: null,
    noOfDays: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Fetch holiday types on component mount
  useEffect(() => {
    fetchHolidayTypes();
  }, []);

  // Fetch holiday details AFTER holidayTypes are loaded (so dropdown can auto-select)
  useEffect(() => {
    if (editId && holidayTypes.length > 0) {
      fetchHolidayDetails();
    }
  }, [editId, holidayTypes]);

  // Auto-calculate No. of Days when dates change
  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const from = dayjs(formData.fromDate);
      const to = dayjs(formData.toDate);
      if (to.isAfter(from) || to.isSame(from)) {
        const days = to.diff(from, "day") + 1;
        setFormData((prev) => ({
          ...prev,
          noOfDays: days.toString(),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          noOfDays: "",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        noOfDays: "",
      }));
    }
  }, [formData.fromDate, formData.toDate]);

  // Reset toDate if it's before fromDate when fromDate changes
  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const from = dayjs(formData.fromDate);
      const to = dayjs(formData.toDate);
      if (to.isBefore(from)) {
        setFormData((prev) => ({
          ...prev,
          toDate: null,
          noOfDays: "",
        }));
        setErrors((prev) => ({
          ...prev,
          toDate:
            translations.labels?.toDateResetWarning ||
            "To Date was reset as it was before From Date",
        }));
      }
    }
  }, [formData.fromDate]);

  // Fetch holiday types for dropdown
  const fetchHolidayTypes = async () => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/LEAVE");

      if (response?.data?.status) {
        // Filter out LEAVE_TYPE and map the options
        const options = response.data.data
          .filter((item) => item.domain_id !== "LEAVE_TYPE")
          .map((item) => ({
            value: item.id,
            label: item.domain_id,
          }));
        setHolidayTypes(options);
      }
    } catch (error) {
      console.error("Error fetching holiday types:", error);
    }
  };

  // Fetch holiday details for edit mode
  const fetchHolidayDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/school/holiday/view/${editId}`,
      );

      if (response?.data?.success) {
        const data = response.data.data[0];

        // Match by domain_id (numeric ID like 227) from view API
        // holidayTypes options have { value: 227, label: "State Holiday" }
        // view API returns { domain_id: 227 }
        const matchedValue = holidayTypes.find(
          (opt) => opt.value === data.domain_id,
        )
          ? data.domain_id
          : "";

        setFormData({
          holidayType: matchedValue,
          fromDate: data.start_date
            ? dayjs(data.start_date).format("YYYY-MM-DD")
            : null,
          toDate: data.end_date
            ? dayjs(data.end_date).format("YYYY-MM-DD")
            : null,
          noOfDays: data.no_of_days ? data.no_of_days.toString() : "",
          description: data.description || "",
        });

        // setModalState({
        //   open: true,
        //   type: "success",
        //   message: response.data?.message,
        //   redirectPath: "",
        // });
      }
    } catch (error) {
      console.error("Error fetching holiday details:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.holidayFetchFailed ||
          "Failed to fetch holiday details. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user selects
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleDateChange = (name, date) => {
    const formattedDate = date ? dayjs(date).format("YYYY-MM-DD") : null;

    // Validation for To Date - should not be before From Date
    if (name === "toDate" && formattedDate && formData.fromDate) {
      if (dayjs(formattedDate).isBefore(dayjs(formData.fromDate))) {
        setErrors((prev) => ({
          ...prev,
          toDate:
            translations.labels?.toDateCannotBeBeforeFrom ||
            "To Date cannot be before From Date",
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedDate,
    }));

    // Clear error when user selects date
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Holiday Type validation
    if (!formData.holidayType) {
      newErrors.holidayType =
        translations.labels?.holidayTypeRequired || "Holiday Type is required";
    }

    // From Date validation
    if (!formData.fromDate) {
      newErrors.fromDate =
        translations.labels?.fromDateRequired || "From Date is required";
    }

    // To Date validation
    if (!formData.toDate) {
      newErrors.toDate =
        translations.labels?.toDateRequired || "To Date is required";
    }

    // To Date must be after or same as From Date
    if (
      formData.fromDate &&
      formData.toDate &&
      dayjs(formData.toDate).isBefore(dayjs(formData.fromDate))
    ) {
      newErrors.toDate =
        translations.labels?.toDateAfterOrSameAsFrom ||
        "To Date must be after or same as From Date";
    }

    // Description validation
    if (!formData.description || formData.description.trim() === "") {
      newErrors.description =
        translations.labels?.descriptionRequired || "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        client_id: client_id,
        school_id: school_id,
        holiday_type:
          formData?.holidayType?.value || formData?.holidayType || "",
        start_date: formData.fromDate,
        end_date: formData.toDate,
        no_of_days: parseInt(formData.noOfDays) || 1,
        description: formData.description,
      };

      let response;

      if (editId) {
        // PUT method for update
        payload.id = editId;
        payload.updated_by = user_id;
        response = await AxiosInstance.put("/school/holiday/update", payload);
      } else {
        // POST method for create
        payload.created_by = user_id;
        response = await AxiosInstance.post("/school/holiday/create", payload);
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message || editId
              ? translations.labels?.holidayUpdateSuccess ||
                "Holiday updated successfully!"
              : translations.labels?.holidayCreateSuccess ||
                "Holiday created successfully!",
          redirectPath: "/user/holidayList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.holidaySaveFailed ||
          "Failed to save holiday. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/holidayList");
  };

  // Get min date for To Date picker (should be From Date or null)
  const getToDateMinDate = () => {
    if (formData.fromDate) {
      return dayjs(formData.fromDate);
    }
    return null;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.holiday || "Holiday"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.holiday || "Holiday"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Holiday Type */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.holiday || "Holiday"}
                name="holidayType"
                value={formData.holidayType}
                onChange={(value) => handleSelectChange("holidayType", value)}
                options={holidayTypes}
                placeholder={
                  translations.labels?.selectLeaveType || "Select Leave Type"
                }
                required
                error={errors.holidayType}
              />
            </div>

            {/* From Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.fromDate || "From Date"}
                name="fromDate"
                value={formData.fromDate ? dayjs(formData.fromDate) : null}
                onChange={(date) => handleDateChange("fromDate", date)}
                placeholder="Ex: 30/07/2016"
                required
                error={errors.fromDate}
              />
            </div>

            {/* To Date */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={translations.labels?.toDate || "To Date"}
                name="toDate"
                value={formData.toDate ? dayjs(formData.toDate) : null}
                onChange={(date) => handleDateChange("toDate", date)}
                placeholder="Ex: 30/07/2016"
                required
                error={errors.toDate}
                minDate={getToDateMinDate()}
                disabled={!formData.fromDate}
              />
            </div>

            {/* No. of Days */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.noOfDays || "No. of Days"}
                name="noOfDays"
                type="text"
                value={formData.noOfDays}
                onChange={handleInputChange}
                placeholder=""
                disabled={true}
                readOnly={true}
              />
            </div>

            {/* Description */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.description || "Description"}
                name="description"
                type="text"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.enterYourMessages ||
                  "E.g. Enter your messages"
                }
                required
                error={errors.description}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                editId
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
            >
              {editId
                ? translations.labels?.updateButton || "UPDATE"
                : translations.labels?.saveButton || "SAVE"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancelButton || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default HolidayCreate;
